import React from "react";
import { Link } from "react-router-dom";
import { Home, Compass } from "lucide-react";
import { SEO } from "../components/seo/SEO";

const LOGO = "/brand-logo.png";

/**
 * Branded "soft 404" page for any unmatched public route.
 * Note: since this is a client-side rendered SPA, the actual HTTP status
 * code returned by the web server for unknown paths is controlled by the
 * hosting/ingress layer (outside this app's code) — this page sets a
 * `noindex` meta tag so Google treats it as non-indexable regardless of
 * the transport status code, per Google's guidance for JS-rendered 404s.
 */
export default function NotFound() {
  return (
    <div className="min-h-screen bg-[#0B1220] text-slate-300 flex flex-col" data-testid="not-found-page">
      <SEO
        title="Page Not Found | ServiceGuru Pro"
        description="The page you're looking for doesn't exist. Explore ServiceGuru Pro's home service CRM, dispatching, estimates, invoicing and AI voice agent features."
        robots="noindex, follow"
      />
      <header className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-5 py-5 flex items-center">
          <Link to="/" className="flex items-center gap-2.5" data-testid="not-found-logo-link">
            <div className="h-9 w-9 rounded-lg bg-white flex items-center justify-center overflow-hidden shrink-0">
              <img src={LOGO} alt="ServiceGuru Pro" className="h-full w-full object-contain" />
            </div>
            <span className="text-white font-bold text-[15px] tracking-tight">ServiceGuru Pro</span>
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto px-5 py-20 text-center">
        <p className="text-blue-400 font-bold text-sm tracking-widest mb-3">404 ERROR</p>
        <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight mb-4" data-testid="not-found-heading">
          We couldn't find that page
        </h1>
        <p className="text-slate-400 max-w-lg mx-auto mb-10">
          The page may have moved or no longer exists. Here are some places to go instead.
        </p>

        <div className="flex flex-wrap items-center justify-center gap-3 mb-14">
          <Link
            to="/"
            data-testid="not-found-home-link"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm transition-colors"
          >
            <Home className="h-4 w-4" /> Back to Home
          </Link>
          <Link
            to="/signup"
            data-testid="not-found-trial-link"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 hover:border-blue-400 text-white font-semibold text-sm transition-colors"
          >
            Start Free Trial
          </Link>
          <Link
            to="/pricing"
            data-testid="not-found-pricing-link"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 hover:border-blue-400 text-white font-semibold text-sm transition-colors"
          >
            View Pricing
          </Link>
        </div>

        <div className="border-t border-white/10 pt-8">
          <p className="text-xs uppercase tracking-widest text-slate-500 mb-4 flex items-center justify-center gap-2">
            <Compass className="h-3.5 w-3.5" /> Popular pages
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
            <Link to="/" data-testid="not-found-link-home" className="text-slate-400 hover:text-blue-400 transition-colors">
              Home
            </Link>
            <Link to="/pricing" data-testid="not-found-link-pricing" className="text-slate-400 hover:text-blue-400 transition-colors">
              Pricing
            </Link>
            <Link to="/become-an-affiliate" data-testid="not-found-link-affiliate" className="text-slate-400 hover:text-blue-400 transition-colors">
              Become an Affiliate
            </Link>
            <Link to="/login" data-testid="not-found-link-login" className="text-slate-400 hover:text-blue-400 transition-colors">
              Log In
            </Link>
            <Link to="/privacy" data-testid="not-found-link-privacy" className="text-slate-400 hover:text-blue-400 transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" data-testid="not-found-link-terms" className="text-slate-400 hover:text-blue-400 transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
